let _ = t => t,
  _t,
  _t2;
import { useContext, useState, useEffect } from 'react';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useFetchResolver } from 'reference-resolvers-lite/ResolverHooks';
import { styledWithTrellis } from '../../../utils/trellis';
import { Link } from 'foundations-components/transitional/components/Link';
import { Box } from 'foundations-components/transitional/components/Box';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Small } from 'foundations-components/transitional/components/Small';
import { ReplyIcon } from 'foundations-assets/icons/components/ReplyIcon';
import { USER } from '../../../constants/CallParticipantTypes';
import { REPLY } from '../../../types/CommentTypes';
import { AvatarWrapper } from '../../shared/AvatarWrapper';
import { CommentHeader } from './CommentHeader';
import { CommentActionButtons } from './CommentActionButtons';
import { CommentsContext } from '../../../contexts/CommentsContext';
import { EditCommentEditor } from './EditCommentEditor';
import { ADVANCED } from '../../../types/CommentsType';
import { AVATAR_SIZE_PX } from '../../../constants/AvatarSizes';
import { getEditCommentStorageKey } from '../../../constants/LocalStorageConstants';
import { useLocalStorageState } from '../../../utils/useLocalStorageState';
import { isStyleOnFill, useCommentsUseStyle } from '../../../contexts/CommentsUseStyleContext';
import { isCompact, isModernized } from '../../../types/CommentDensityType';
import { formatMonthDayYearAtTimeString, formatTimeAgo } from '../../../utils/dateTimeFormatUtils';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getHoverBackground = (theme, isOnFill, hoverBackgroundColor) => {
  var _theme$color;
  if (isOnFill) return 'unset';
  return (_theme$color = theme.color[hoverBackgroundColor]) !== null && _theme$color !== void 0 ? _theme$color : theme.color['fill-tertiary-hover'];
};
const StyledCommentContainer = styledWithTrellis.div(_t || (_t = _`
  border-radius: ${0};
  border: unset;
  box-sizing: border-box;
  display: flex;
  margin-inline-start: ${0};
  padding-block-end: ${0};
  padding-block-start: ${0};
  padding-inline: ${0};
  position: relative;
  width: unset;
  z-index: 4;
  &:hover {
    background: ${0};
    z-index: ${0};
  }
`), ({
  theme
}) => theme.borderRadius['200'], ({
  marginInlineStart,
  theme
}) => theme.space[marginInlineStart], ({
  theme,
  density = 'default'
}) => isCompact(density) ? theme.space['100'] : theme.space['300'], ({
  theme,
  density = 'default'
}) => isCompact(density) ? theme.space['200'] : theme.space['300'], ({
  theme,
  flushInline
}) => flushInline ? theme.space['200'] : theme.space['300'], ({
  theme,
  isOnFill,
  hoverBackgroundColor,
  hasActions
}) => hasActions ? getHoverBackground(theme, isOnFill, hoverBackgroundColor) : 'unset', ({
  hasActions
}) => hasActions ? 1 : 4);
const StyledSanitizedTextWrapper = styledWithTrellis.div(_t2 || (_t2 = _`
  border-radius: ${0};
   background-color: ${0};
  padding-inline: ${0};
  margin-inline: ${0};
  display: block;
  overflow-wrap: anywhere;
  transition: ${0};
`), ({
  theme
}) => theme.borderRadius['200'], ({
  highlight,
  theme,
  isOnFill
}) => isOnFill ? 'unset' : highlight ? theme.color['fill-accent-yellow-hover'] : 'unset', ({
  theme
}) => theme.space['200'], ({
  theme
}) => `-${theme.space['200']}`, ({
  isOnFill
}) => isOnFill ? 'none' : 'background-color 1s ease');
export const Comment = props => {
  const {
    commentData,
    showReplyEditor,
    updateShowReplyEditor,
    anchor,
    showReplyLink = true,
    variant
  } = props;
  const {
    currentUserId,
    shouldDisableReplying,
    highlightCommentId,
    density,
    commentLayoutVariant,
    hoverBackgroundColor,
    editSubmitBtnText,
    avatarConfig: {
      size: avatarSize,
      flushInline
    }
  } = useContext(CommentsContext);
  const modernized = isModernized(commentLayoutVariant);
  const use = useCommentsUseStyle();
  const isOnFill = isStyleOnFill(use);
  const editCommentStorageKey = getEditCommentStorageKey({
    objectType: commentData.objectType,
    objectId: commentData.objectId,
    commentId: commentData.id
  });
  const [editCommentDraft] = useLocalStorageState({
    key: editCommentStorageKey,
    initialValue: undefined
  });
  const resolver = createStandardCachedResolver(USER);
  const {
    status,
    reference
  } = useFetchResolver(resolver, commentData.userId.toString());
  const [isHovered, setIsHovered] = useState(false);
  const [showEditCommentEditor, setShowEditCommentEditor] = useState(editCommentDraft !== undefined);
  const name = status === 'success' && reference ? reference.label : '';
  const email = status === 'success' && reference ? reference.description : '';
  const [isHighlighted, setIsHighlighted] = useState(false);
  const shouldHighlight = commentData.id === String(highlightCommentId);
  useEffect(() => {
    if (shouldHighlight) {
      setIsHighlighted(true);
      const timeout = setTimeout(() => {
        setIsHighlighted(false);
      }, 5000);
      return () => clearTimeout(timeout);
    } else {
      setIsHighlighted(false);
    }
  }, [shouldHighlight]);
  const hasEditDeleteActions = variant === ADVANCED && commentData.userId === currentUserId;
  const hasReplyAction = variant === ADVANCED && !shouldDisableReplying && showReplyLink && !showReplyEditor && !showEditCommentEditor;
  const hasActions = modernized ? hasEditDeleteActions || hasReplyAction : hasEditDeleteActions;
  const compact = isCompact(density);
  const buttonSize = compact ? 'sm' : 'md';
  const replyIconButton = hasReplyAction ? /*#__PURE__*/_jsx(Tooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "objectCommentsThread.comments.reply"
    }),
    children: /*#__PURE__*/_jsx(IconButton, {
      "data-test-id": "comments-replyButton",
      use: "transparent",
      size: buttonSize,
      onClick: () => updateShowReplyEditor(true),
      margin: "0",
      padding: "0",
      children: /*#__PURE__*/_jsx(ReplyIcon, {})
    })
  }) : undefined;
  const getModernizedDateDisplay = () => {
    if (compact) {
      return /*#__PURE__*/_jsx(FormattedDateTime, {
        value: I18n.moment(commentData.createdAt),
        format: "lll",
        type: "userTz"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "objectCommentsThread.comments.timeSpanAgo",
      options: {
        timeSpan: formatTimeAgo(commentData.createdAt)
      }
    });
  };
  const modernizedDateDisplay = modernized ? /*#__PURE__*/_jsx(Tooltip, {
    title: formatMonthDayYearAtTimeString(commentData.createdAt),
    "data-test-id": "comment-date-tooltip",
    children: /*#__PURE__*/_jsxs(Small, {
      use: "help",
      children: [getModernizedDateDisplay(), commentData.createdAt !== commentData.updatedAt && /*#__PURE__*/_jsx(Tooltip, {
        title: formatMonthDayYearAtTimeString(commentData.updatedAt),
        children: /*#__PURE__*/_jsx(Text, {
          as: "span",
          display: "inline-block",
          marginInlineStart: "100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "objectCommentsThread.comments.edited"
          })
        })
      })]
    })
  }) : null;
  return (
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsxs(StyledCommentContainer, {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      "data-test-id": `comment-${commentData.id}`,
      id: `comment-${commentData.id}`,
      marginInlineStart: commentData.type === REPLY ? '1000' : '0',
      "data-selected": shouldHighlight,
      isOnFill: isOnFill,
      density: density,
      hoverBackgroundColor: hoverBackgroundColor,
      flushInline: flushInline,
      hasActions: hasActions,
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 0,
        shrink: 0,
        basis: AVATAR_SIZE_PX[avatarSize],
        marginInlineEnd: flushInline ? '200' : '300',
        children: /*#__PURE__*/_jsx(AvatarWrapper, {
          objectId: commentData.userId,
          email: email,
          displayName: name,
          objectType: USER,
          size: avatarSize
        })
      }), /*#__PURE__*/_jsx(Box, {
        grow: 1,
        basis: "100%",
        minInlineSize: "0",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsxs(Flex, {
            justifyContent: "space-between",
            inlineSize: "100%",
            children: [/*#__PURE__*/_jsx(Box, {
              grow: 1,
              shrink: 0,
              children: /*#__PURE__*/_jsx(CommentHeader, {
                status: status,
                name: name,
                comment: commentData,
                anchor: anchor,
                compact: compact,
                hideDate: modernized
              })
            }), isHovered && !showEditCommentEditor && hasActions && /*#__PURE__*/_jsx(Box, {
              grow: 0,
              shrink: 0,
              children: modernized ? /*#__PURE__*/_jsxs(_Fragment, {
                children: [hasReplyAction && replyIconButton, hasEditDeleteActions && /*#__PURE__*/_jsx(CommentActionButtons, {
                  commentId: commentData.id,
                  setShowEditCommentEditor: setShowEditCommentEditor
                })]
              }) : /*#__PURE__*/_jsx(CommentActionButtons, {
                commentId: commentData.id,
                setShowEditCommentEditor: setShowEditCommentEditor
              })
            }), modernized && modernizedDateDisplay]
          }), /*#__PURE__*/_jsxs(Box, {
            "data-test-id": "comments-messageContainer",
            inlineSize: "100%",
            children: [showEditCommentEditor ? /*#__PURE__*/_jsx(EditCommentEditor, {
              comment: commentData,
              onSubmitCallback: () => setShowEditCommentEditor(false),
              onCancelCallback: () => setShowEditCommentEditor(false),
              editSubmitBtnText: editSubmitBtnText
            }) :
            /*#__PURE__*/
            /* @bend-expect trellis-tokenized */
            _jsx(StyledSanitizedTextWrapper, {
              highlight: isHighlighted,
              isOnFill: isOnFill,
              children: /*#__PURE__*/_jsx(SanitizedText, {
                body: commentData.message
              })
            }), !modernized && variant === ADVANCED && !shouldDisableReplying && showReplyLink && !showReplyEditor && !showEditCommentEditor && /*#__PURE__*/_jsx(Link, {
              "data-test-id": "comments-replyButton",
              onClick: () => updateShowReplyEditor(true),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "objectCommentsThread.comments.reply"
              })
            })]
          })]
        })
      })]
    })
  );
};